<template>
  <vl-map
    ref="map"
    data-cy="traffic-map"
    class="map"
    :load-tiles-while-animating="true"
    :load-tiles-while-interacting="true"
    data-projection="EPSG:3857"
  >
    <vl-view
      ref="mapView"
      :zoom.sync="zoom"
      :center.sync="center"
      :rotation.sync="rotation"
    ></vl-view>

    <vl-layer-tile
      v-for="layer in baseLayers"
      :id="layer.name"
      :key="layer.name"
      :visible="layer.visible"
    >
      <vl-source-xyz v-bind="layer" :url="layer.url" />
    </vl-layer-tile>
    <vl-layer-tile v-if="selectedLayer" :visible="selectedLayer.wmsVisible">
      <vl-source-wms
        url="https://wms.geo.admin.ch/?version=1.3.0&service=WMS&request=GetLegendGraphic&sld_version=1.1.0&layer=ch.swisstopo.amtliches-strassenverzeichnis&format=image/png&STYLE=default"
        layers="ch.swisstopo.amtliches-strassenverzeichnis"
      />
    </vl-layer-tile>
    <div class="layers-panel">
      <div class="buttons">
        <button
          v-for="layer in baseLayers"
          :key="layer.name"
          class="button mrgl-"
          :class="{ 'is-info': layer.visible }"
          @click="showBaseLayer(layer.name)"
        >
          {{ layer.title }}
        </button>
      </div>
    </div>

    <EntityMarker
      v-for="heartbeat of heartbeats"
      :key="heartbeat.username"
      :feature-id="`feature-${heartbeat.username}`"
      :username="heartbeat.username"
      :longitude="heartbeat.longitude"
      :latitude="heartbeat.latitude"
    />
    <EntityMarker
      v-if="lastKnownHeartbeat"
      :key="lastKnownHeartbeat.username"
      feature-id="lastKnownHeartbeatMarker"
      :username="lastKnownHeartbeat.username"
      :longitude="lastKnownHeartbeat.longitude"
      :latitude="lastKnownHeartbeat.latitude"
    />
    <vl-interaction-select
      ref="interactionSelect"
      :features.sync="selectedFeatures"
    />
    <vl-overlay
      v-for="feature in selectedFeatures"
      :id="feature.id"
      :key="feature.id"
      :position="pointOnSurface(feature.geometry)"
      :offset="[0, -25]"
    >
      <EntityPopover :username="feature.properties.username" />
    </vl-overlay>
  </vl-map>
</template>

<script>
import Vue from 'vue';

import eventBus from '@/eventBus.js';

import VueLayers from 'vuelayers';
import 'vuelayers/lib/style.css';

Vue.use(VueLayers);

import { findPointOnSurface } from 'vuelayers/lib/ol-ext';

import { mapState } from 'vuex';

import heartbeatSocketMixin from '@/mixins/heartbeatSocketMixin';

import EntityPopover from '../common/EntityPopover';
import EntityMarker from './EntityMarker';
import { fromLonLat } from 'ol/proj';

export default {
  components: { EntityPopover, EntityMarker },

  mixins: [heartbeatSocketMixin],

  data() {
    return {
      baseLayers: [
        {
          name: 'map',
          title: 'Carte',
          url:
            'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg',
          visible: true,
          wmsVisible: false,
        },
        {
          name: 'satellite',
          title: 'Hybride',
          url:
            'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg',
          visible: false,
          wmsVisible: true,
        },
      ],
      rotation: 0,
      center: [
        parseFloat(process.env.VUE_APP_MAP_DEFAULT_LONGITUDE),
        parseFloat(process.env.VUE_APP_MAP_DEFAULT_LATITUDE),
      ],
      zoom: parseFloat(process.env.VUE_APP_MAP_DEFAULT_ZOOM),
      selectedFeatures: [],
      selectedLayer: null,
    };
  },

  computed: {
    ...mapState('live', ['heartbeats', 'lastKnownHeartbeat']),
  },

  mounted() {
    eventBus.$on('panTo', this.handlePanTo);

    this.selectedLayer = this.baseLayers[0];
  },

  beforeDestroy() {
    eventBus.$off('panTo', this.handlePanTo);
  },

  methods: {
    pointOnSurface: findPointOnSurface,

    /**
     * Center the map with animation when global event `panTo` is fired.
     */
    handlePanTo(location, openPopover, username) {
      this.$refs.mapView.animate({
        center: fromLonLat([location.lng, location.lat]),
        zoom: 18,
      });
      if (openPopover) {
        this.selectedFeatures = [];
        setTimeout(() => {
          let featureId;
          if (username == null) {
            featureId = 'lastKnownHeartbeatMarker';
          } else {
            featureId = 'feature-' + username;
          }
          const feature = this.$refs.map.$featuresCollection.array_.find(
            e => e.id_ === featureId
          );
          this.$refs.interactionSelect.select(feature);
        }, 1000);
      }
    },

    showBaseLayer(name) {
      let layer = this.baseLayers.find(layer => layer.visible);
      if (layer != null) {
        layer.visible = false;
      }
      layer = this.baseLayers.find(layer => layer.name === name);
      if (layer != null) {
        layer.visible = true;
      }
      this.selectedLayer = layer;
    },
  },
};
</script>
<style lang="scss" scoped>
.map {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.layers-panel {
  position: absolute;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  z-index: 1;
}
</style>
